import Button from '@material-ui/core/Button';
import CancelIcon from '@material-ui/icons/Cancel';
import CircularProgress from '@material-ui/core/CircularProgress';
import { DepartmentSelect } from '../Details/DepartmentSelect';
import RouteIcon from '@mui/icons-material/Route';
import MapIcon from '@material-ui/icons/Map';
import { EditWorkshopRequest } from 'types/api/request';
import { Environment } from 'utils/environment';
import Grid from '@material-ui/core/Grid';
import { NumericInput } from '../Details/NumericInput';
import Paper from '@material-ui/core/Paper';
import { ReadOnlyField } from '../Details/ReadOnlyField';
import { SelectInput } from '../Details/SelectInput';
import { shouldReload } from 'utils/state.handlers';
import { Styles } from '../Tools';
import { TextInput } from '../Details/TextInput';
import { updateWorkshop } from 'redux/actions/workshop.actions';
import { useFormik } from 'formik';
import {
  fetchOffices,
  fetchProviders,
  fetchWorkshopTypes,
} from 'redux/actions/catalog.actions';
import {
  FormControlLabel,
  Switch,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { getVerification, toIsoDate, workshopValues } from 'utils';
import { getWorkshopStatus, Workshop } from 'types/api/responses/workshop';
import { Municipio, Office } from 'types/api/responses/zones';
import { Provider, WorkshopType } from 'types/api/responses';
import React, { useState, useEffect } from 'react';
import { useThunkDispatch, useTypedSelector } from 'hooks';
import { Verification, verifications, Kind, kinds } from 'config';
import * as yup from 'yup';
import '../Details/gridDetails.css';
import { Save } from '@material-ui/icons';
import { RenderIf } from 'components/Tools/Utils';
import { Padding } from '@mui/icons-material';
import { format, parse } from 'date-fns';

const validationSchema = (isMatriz: Boolean) => {
  return {
    name: yup.string().required('Nombre es requerido').min(5, 'Minimo 5 caracteres'),
    branchOfficeId: yup.string().required('Requerido'),
    municipioId: yup.string().required('Debe seleccionar un municipio'),
    clientAccount: yup
      .string()
      .length(7, 'Debe tener exactamente 7 digitos')
      .matches(new RegExp(/[0-9]{7}/), 'Formato invalido')
      .nullable(),
    potential: yup.string().required('Requerido'),
    providerId: yup
      .number()
      .min(1, 'Debe seleccionar proveedor')
      .required('Debe seleccionar proveedor'),
    seller: yup.string().nullable(),
    ownerCellphone: yup
      .string()
      .matches(new RegExp(/^\d{8}$/), 'Debe tener exactamente 8 digitos')
      .matches(/^[^013].*/, 'No puede iniciar con 0, 1 o 3')
      .nullable()
      .required('Requerido'),
    ownerLastName: yup
      .string()
      .min(3, 'Minimo 3 caracteres')
      .when('kind', {
        is: isMatriz,
        then: (schema) => schema.required('Requerido'),
      }),
    ownerName: yup
      .string()
      .min(3, 'Minimo 3 caracteres')
      .when('kind', {
        is: isMatriz,
        then: (schema) => schema.required('Requerido'),
      }),
    ownerEmail: yup
      .string()
      .nullable()
      .email('Formato invalido')
      .when('kind', {
        is: isMatriz,
        then: (schema) => schema.required('Requerido'),
      }),
    buyer: yup.string().nullable().required('Requerido').min(3, 'Minimo 3 caracteres'),
    buyerLastName: yup.string().nullable().required('Requerido').min(3, 'Minimo 3 caracteres'),
    buyerCellphone: yup
      .string()
      .matches(new RegExp(/^\d{8}$/), 'Debe tener exactamente 8 digitos')
      .matches(/^[^013].*/, 'No puede iniciar con 0, 1 o 3')
      .nullable()
      .required('Requerido'),
    email: yup.string().nullable().email('Formato invalido'),
    address: yup.string().nullable().required('Requerido').min(10, 'Minimo 10 caracteres'),
    mechanics: yup.number().min(workshopValues.mechanics.min),
    workshopTypeId: yup
      .number()
      .min(1, 'Debe seleccionar proveedor')
      .required('Debe seleccionar proveedor'),
    diagnostics: yup.boolean().required('Requerido'),
    tools: yup.boolean().required('Requerido'),
    kind: yup.string().nullable().required('Requerido'),
  } as const;
};
const locationValidationSchema = (country: string) => {
  switch (country) {
    case 'SV':
      return {
        longitude: yup
          .number()
          .min(-90.1512, 'Mínimo -90.1512')
          .max(-87.6727, 'Máximo -87.6727')
          .required('Longitud requerida'),
        latitude: yup
          .number()
          .min(13.1017, 'Mínimo 13.1017')
          .max(14.4816, 'Máximo 14.4816')
          .required('Latitud requerida'),
      };
    case 'HN':
      return {
        longitude: yup
          .number()
          .min(-89.4123, 'Mínimo -89.4123')
          .max(-82.9821, 'Máximo -82.9821')
          .required('Longitud requerida'),
        latitude: yup
          .number()
          .min(12.9163, 'Mínimo 12.9163')
          .max(16.0808, 'Máximo 16.0808')
          .required('Latitud requerida'),
      };
    case 'NI':
      return {
        longitude: yup
          .number()
          .min(-87.7877, 'Mínimo -87.7877')
          .max(-83.0594, 'Máximo -83.0594')
          .required('Longitud requerida'),
        latitude: yup
          .number()
          .min(10.6393, 'Mínimo 10.6393')
          .max(15.0911, 'Máximo 15.0911')
          .required('Latitud requerida'),
      };
    default:
      return {
        longitude: yup.number().required('Longitud requerida'),
        latitude: yup.number().required('Latitud requerida'),
      };
  }
};

export type Result = 'success' | 'error' | undefined;

type WorkshopDetailsProps = {
  shop: Workshop;
  onClose: (result: Result) => void;
};

export function DetailsWorkshop({ shop, onClose }: WorkshopDetailsProps) {
  const classes = Styles();
  const dispatch = useThunkDispatch();
  const { workshopTypes, offices, providers } = useTypedSelector((s) => s.catalog);
  const { currentId, departments } = useTypedSelector((s) => s.departments);
  const isLoading = useTypedSelector((w) => w.workshop.isLoading);
  const profile = useTypedSelector((u) => u.profile.profile);
  const myRole = useTypedSelector((s) => s.session.user.role.accessLevel);
  const allowProviders = providers.data.filter((p) => p.active || p.id === shop.provider?.id);
  const allowTypes = workshopTypes.data.filter(
    (t) => t.active || t.id === shop.workShopType?.id,
  );
  const municipio = departments.find((d) => d.id === currentId)?.municipios.data || [];

  const geoSchema = locationValidationSchema(Environment.Country);

  const isMatriz = shop.kind === 'Matrix';

  const formik = useFormik<EditWorkshopRequest>({
    initialValues: {
      ...shop,
      branchOfficeId: shop.branchOffice.id,
      municipioId: shop.municipio?.id,
      providerId: shop.provider?.id,
      workshopTypeId: shop.workShopType?.id,
    },
    validateOnChange: false,
    validateOnBlur: true,
    validateOnMount: true,
    validationSchema: yup.object({
      ...validationSchema(isMatriz),
      ...geoSchema,
      weeklyCars: yup.number().when('workshopTypeId', {
        is: (val: number | undefined) => {
          const found = workshopTypes.data.find((t) => t.id === val);
          return val && found?.external;
        },
        then: (schema) => schema.min(1).max(100).required('Requerido'),
      }),
    }),
    onSubmit: (values) => {
      dispatch(updateWorkshop(values))
        .then(() => onClose('success'))
        .catch(() => onClose('error'));
    },
  });
  const external = workshopTypes.data.find((t) => t.id === formik.values.workshopTypeId);
  const baseURL = Environment.baseUrl;
  const isCoordinator = myRole !== 5;
  const [edit, setEdit] = useState(false);
  useEffect(() => {
    if (shouldReload(offices) && edit) {
      dispatch(fetchOffices());
    }
  }, [edit, offices, dispatch]);

  useEffect(() => {
    if (shouldReload(providers) && edit) {
      dispatch(fetchProviders());
    }
  }, [edit, providers, dispatch]);

  useEffect(() => {
    if (shouldReload(workshopTypes)) {
      dispatch(fetchWorkshopTypes());
    }
  }, [workshopTypes, dispatch]);

  /*  useEffect(() => {
    const isZero = formik.values.longitude === 0 && formik.values.latitude === 0;
    if (!isZero && external?.external) {
      formik.setFieldValue('longitude', 0, false);
      formik.setFieldValue('latitude', 0, false);
    }
  }, [external, formik]);*/

  const handleEdit = () => {
    if (edit) {
      onCloseEdit();
      return;
    }
    setEdit(true);
  };

  const onCloseEdit = () => {
    formik.resetForm();
    setEdit(false);
  };

  const showNullReadOnly = false;

  const sortedTracks = [...(shop.tracks || [])].sort((a, b) => a.priority - b.priority);

  const formatAccount = (account: string | undefined) => {
    if (!account) return '';

    return account + '-' + (isMatriz ? '0' : shop.kindNumber);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    const parsedDate = parse(dateString, 'yyyyMMddHHmmss', new Date());
    return format(parsedDate, 'dd-MM-yyyy HH:mm:ss');
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container xl={true} spacing={3}>
        <div className={classes.flexRowCenter}>
          <Paper
            className={classes.paper}
            style={{
              backgroundColor: getWorkshopStatus(shop.status)?.color,
              color: getWorkshopStatus(shop.status)?.fix,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: '5px',
              padding: '5px',
              borderRadius: '5px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
              maxHeight: '100%',
              fontSize: '24px',
              fontWeight: 'bold',
              textAlign: 'center',
              textTransform: 'uppercase',
            }}
          >
            {shop.name}
          </Paper>
          <Button
            variant="outlined"
            onClick={() => onClose(undefined)}
            style={{
              color: '#dc3545',
              marginRight: '5px',
              fontSize: '24px',
            }}
          >
            <CancelIcon />
          </Button>
        </div>
      </Grid>

      {/* INFORMACIÓN BÁSICA */}
      <Accordion defaultExpanded style={{ marginTop: '20px', marginBottom: '10px' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" style={{ fontWeight: 'bold' }}>
            Información Básica
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <div
            className="form-container"
            style={{ width: '100%', display: 'flex', alignItems: 'center' }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: '20px',
                width: '100%',
              }}
            >
              <Card
                style={{
                  maxWidth: '750px',
                  width: '100%',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                  borderRadius: '10px',
                  overflow: 'hidden',
                }}
              >
                <img
                  src={baseURL + shop.pictureUrl}
                  alt={shop.name}
                  style={{
                    width: '100%',
                    height: '450px',
                    objectFit: 'cover',
                  }}
                />
              </Card>
            </div>
            <div className="form-container-inner" style={{ width: '100%' }}>
              <TextInput
                unique
                label="Nombre Comercial"
                className="half"
                shopKey="name"
                form={formik}
                inputProps={{ maxLength: workshopValues.name, readOnly: !edit }}
              />

              <SelectInput<WorkshopType>
                none={0}
                label="Tipo de Actividad Taller"
                form={formik}
                readonly={!edit}
                readonlyValue={shop.workShopType?.name}
                fieldId="workshopTypeId"
                keyValue="name"
                options={allowTypes}
              />
            </div>
          </div>
        </AccordionDetails>
      </Accordion>

      {/* PERFILADO DEL CLIENTE */}
      <Accordion style={{ marginBottom: '10px' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" style={{ fontWeight: 'bold' }}>
            Perfilado del Cliente
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <div className="form-container" style={{ width: '100%' }}>
            <SelectInput<Kind>
              none=""
              fieldId="kind"
              label="Jerarquía"
              form={formik}
              options={kinds}
              readonlyValue={kinds.find((k) => k.id === shop.kind)?.name}
              keyValue="name"
              readonly={!edit}
            />

            <RenderIf condition={isMatriz}>
              <NumericInput
                label="Cantidad Sucursal"
                numKey="kindAmount"
                form={formik}
                inputProps={{ max: 100, readOnly: !edit, min: 0 }}
              />
            </RenderIf>

         {/*    <RenderIf condition={!isMatriz}>
              <ReadOnlyField
                label="Nombre S/Registro"
                value={shop?.kindNumber?.toString()}
                showNull={showNullReadOnly}
              />
            </RenderIf> */}

            <ReadOnlyField
              label="Cuenta"
              value={formatAccount(profile?.clientAccount)}
              showNull={showNullReadOnly}
            />

            <ReadOnlyField
              label="No. Carnet"
              value={profile?.license}
              showNull={showNullReadOnly}
            />

            <ReadOnlyField
              label="Segmento"
              value={profile?.program}
              showNull={showNullReadOnly}
            />

            <DepartmentSelect value={shop.municipio?.department} disable={!edit} />

            <SelectInput<Municipio>
              none=""
              label="Municipio"
              form={formik}
              readonly={!edit}
              readonlyValue={shop.municipio?.name}
              fieldId="municipioId"
              keyValue="name"
              options={municipio}
            />

            <TextInput
              label="Dirección Taller"
              form={formik}
              className="form-read-only-full"
              shopKey="address"
              profile={profile}
              inputProps={{
                maxLength: workshopValues.address,
                readOnly: !edit,
              }}
            />

            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <NumericInput
                label="Longitud"
                numKey="longitude"
                form={formik}
                step={0.005}
                inputProps={{ readOnly: !edit }}
              />
              <NumericInput
                label="Latitud"
                numKey="latitude"
                form={formik}
                step={0.005}
                inputProps={{ readOnly: !edit }}
              />

              <a
                href={`https://google.com/maps/search/?api=1&query=${formik.values.latitude},${formik.values.longitude}`}
                target="_blank"
              >
                <Button
                  variant="contained"
                  color="primary"
                  style={{
                    color: 'white',
                    height: '60px',
                    width: '50px',
                    borderRadius: '50%',
                  }}
                >
                  <MapIcon />
                </Button>
              </a>
            </div>
          </div>
        </AccordionDetails>
      </Accordion>

      {/* REFERENCIAS DE CONTACTO */}
      <Accordion style={{ marginBottom: '10px' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" style={{ fontWeight: 'bold' }}>
            Referencias de Contacto
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <div className="form-container" style={{ width: '100%' }}>
            {/* PROPIETARIO */}
            <Card style={{ width: '100%', marginBottom: '20px', padding: '15px' }}>
              <Typography
                variant="h6"
                style={{ marginBottom: '15px', fontWeight: 'bold', color: '#343c89' }}
              >
                Propietario
              </Typography>
              <div className="form-container">
                <TextInput
                  label="Apellidos Propietario"
                  shopKey="ownerLastName"
                  form={formik}
                  profile={profile}
                  inputProps={{ maxLength: workshopValues.ownerLastName, readOnly: !edit }}
                />

                <TextInput
                  label="Nombres Propietario"
                  shopKey="ownerName"
                  form={formik}
                  profile={profile}
                  inputProps={{ maxLength: workshopValues.ownerName, readOnly: !edit }}
                />

                <TextInput
                  label="Celular Propietario"
                  shopKey="ownerCellphone"
                  form={formik}
                  profile={profile}
                  inputProps={{ maxLength: workshopValues.ownerCellphone, readOnly: !edit }}
                />

                <TextInput
                  unique
                  label="Email Propietario"
                  shopKey="ownerEmail"
                  form={formik}
                  inputProps={{ maxLength: workshopValues.ownerEmail, readOnly: !edit }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      id="isOwnerManager"
                      checked={formik.values.isOwnerManager}
                      onChange={formik.handleChange}
                      disabled={!edit}
                    />
                  }
                  label="Propietario es jefe de taller"
                />
              </div>
            </Card>

            {/* JEFE */}
            <Card style={{ width: '100%', marginBottom: '20px', padding: '15px' }}>
              <Typography
                variant="h6"
                style={{ marginBottom: '15px', fontWeight: 'bold', color: '#343c89' }}
              >
                Jefe
              </Typography>
              <div className="form-container">
                <TextInput
                  label="Apellidos Jefe"
                  shopKey="managerLastName"
                  form={formik}
                  profile={profile}
                  inputProps={{ readOnly: !edit }}
                />

                <TextInput
                  label="Nombres Jefe"
                  shopKey="managerName"
                  form={formik}
                  profile={profile}
                  inputProps={{ readOnly: !edit }}
                />

                <TextInput
                  label="Celular Jefe"
                  shopKey="managerCellphone"
                  form={formik}
                  profile={profile}
                  inputProps={{ readOnly: !edit }}
                />

                <TextInput
                  unique
                  label="Email Jefe"
                  shopKey="managerEmail"
                  form={formik}
                  inputProps={{ readOnly: !edit }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      id="isOwnerSeller"
                      checked={formik.values.isOwnerSeller}
                      onChange={formik.handleChange}
                      disabled={!edit}
                    />
                  }
                  label="Propietario es comprador"
                />

                <FormControlLabel
                  control={
                    <Switch
                      id="isManagerSeller"
                      checked={formik.values.isManagerSeller}
                      onChange={formik.handleChange}
                      disabled={!edit}
                    />
                  }
                  label="Jefe de taller es comprador"
                />
              </div>
            </Card>

            {/* COMPRADOR */}
            <Card style={{ width: '100%', padding: '15px' }}>
              <Typography
                variant="h6"
                style={{ marginBottom: '15px', fontWeight: 'bold', color: '#343c89' }}
              >
                Comprador
              </Typography>
              <div className="form-container">
                <TextInput
                  label="Apellidos Comprador"
                  shopKey="buyerLastName"
                  form={formik}
                  profile={profile}
                  inputProps={{ maxLength: workshopValues.buyer, readOnly: !edit }}
                />

                <TextInput
                  label="Nombres Comprador"
                  shopKey="buyer"
                  form={formik}
                  profile={profile}
                  inputProps={{ maxLength: workshopValues.buyer, readOnly: !edit }}
                />

                <TextInput
                  label="Celular Comprador"
                  profile={profile}
                  shopKey="buyerCellphone"
                  form={formik}
                  inputProps={{ maxLength: workshopValues.phone, readOnly: !edit }}
                />

                <TextInput
                  unique
                  label="Email Comprador"
                  form={formik}
                  shopKey="email"
                  inputProps={{ maxLength: workshopValues.email, readOnly: !edit }}
                />
              </div>
            </Card>
          </div>
        </AccordionDetails>
      </Accordion>

      {/* ASIGNACIÓN DE SUCURSAL */}
      <Accordion style={{ marginBottom: '10px' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" style={{ fontWeight: 'bold' }}>
            Asignación de Sucursal
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <div className="form-container" style={{ width: '100%' }}>
            <ReadOnlyField
              label="Zona"
              value={shop.branchOffice.zone.name}
              showNull={showNullReadOnly}
            />

            <SelectInput<Office>
              fieldId="branchOfficeId"
              label="Sucursal"
              form={formik}
              options={offices.data}
              keyValue="name"
              readonly={!edit}
              readonlyValue={shop.branchOffice.name}
            />

            <ReadOnlyField showNull={showNullReadOnly} label="Vendedor" value={shop?.seller} />
          </div>
        </AccordionDetails>
      </Accordion>

      {/* CARACTERÍSTICAS DEL TALLER */}
      <Accordion style={{ marginBottom: '10px' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" style={{ fontWeight: 'bold' }}>
            Características del Taller
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <div className="form-container" style={{ width: '100%' }}>
            <NumericInput
              label="Cant. Mecánicos"
              numKey="mechanics"
              form={formik}
              profile={profile}
              inputProps={{ max: 100, readOnly: !edit, min: 1 }}
            />

            <NumericInput
              label="Cant. Auxiliares"
              numKey="assistants"
              form={formik}
              profile={profile}
              inputProps={{ max: 100, readOnly: !edit, min: 0 }}
            />

            <NumericInput
              label="Cant. Elevadores"
              numKey="capacity"
              form={formik}
              profile={profile}
              inputProps={{ max: 100, readOnly: !edit, min: 0 }}
            />

            <SelectInput<Provider>
              none={0}
              fieldId="providerId"
              label="Proveedor Principal"
              form={formik}
              options={allowProviders}
              keyValue="name"
              readonly={!edit}
              readonlyValue={shop.provider?.name}
            />

            <FormControlLabel
              control={
                <Switch
                  id="tools"
                  checked={formik.values.tools}
                  onChange={formik.handleChange}
                  disabled={!edit}
                />
              }
              label="Compra lubricante en mayoreo"
            />

            <FormControlLabel
              control={
                <Switch
                  id="diagnostics"
                  checked={formik.values.diagnostics}
                  onChange={formik.handleChange}
                  disabled={!edit}
                />
              }
              label="Eq. de Diagnostico"
            />
          </div>
        </AccordionDetails>
      </Accordion>

      {/* POTENCIAL DEL TALLER */}
      <Accordion style={{ marginBottom: '10px' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" style={{ fontWeight: 'bold' }}>
            Potencial del Taller
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <div className="form-container" style={{ width: '100%' }}>
            <SelectInput<Verification>
              fieldId="potential"
              label="Potencial Taller"
              form={formik}
              options={verifications}
              keyValue="name"
              readonly={!edit}
              readonlyValue={getVerification(shop.potential)}
            />
          </div>
        </AccordionDetails>
      </Accordion>

      {/* INFORMACIÓN ADICIONAL */}
      <Accordion style={{ marginBottom: '10px' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" style={{ fontWeight: 'bold' }}>
            Información Adicional
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <div className="form-container" style={{ width: '100%' }}>
            <RenderIf condition={shop.tracks !== null && shop?.tracks?.length > 0}>
              <Card style={{ width: '100%', marginBottom: '15px', padding: '15px' }}>
                <Typography
                  variant="h6"
                  style={{ marginBottom: '10px', fontWeight: 'bold', color: '#343c89' }}
                >
                  Rutas
                </Typography>
                {sortedTracks.map((track) => (
                  <p
                    style={{
                      margin: '5px 0',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    key={track?.trackCode}
                  >
                    <RouteIcon style={{ marginRight: '5px' }} />
                    {` ${track?.trackCode}`}
                    <strong style={{ marginLeft: '5px' }}>#{track?.priority}</strong>
                  </p>
                ))}
              </Card>
            </RenderIf>

            <RenderIf condition={shop.userCreator !== null}>
              <ReadOnlyField
                showNull={showNullReadOnly}
                label="Usuario creador"
                value={`${shop.userCreator} ${formatDate(shop.dateCreator)}`}
              />
            </RenderIf>

            <RenderIf condition={shop.userProfiler !== null}>
              <ReadOnlyField
                showNull={showNullReadOnly}
                label="Usuario perfilador"
                value={`${shop.userProfiler} ${formatDate(shop.dateProfiler)}`}
              />
            </RenderIf>

            <RenderIf condition={shop.userVerifier !== null}>
              <ReadOnlyField
                showNull={showNullReadOnly}
                label="Usuario verificador"
                value={`${shop.userVerifier} ${formatDate(shop.dateVerifier)}`}
              />
            </RenderIf>

            <ReadOnlyField
              label="Ultima Actualización"
              value={toIsoDate(shop.lastSync)}
              showNull={showNullReadOnly}
            />
          </div>
        </AccordionDetails>
      </Accordion>

      <div className="form-bottom-control">
        {isLoading && <CircularProgress />}
        {edit && (
          <>
            <Button
              variant="outlined"
              type="submit"
              className={classes.submit}
              style={{ color: '#343c89', marginRight: '5px' }}
              disabled={isLoading}
              startIcon={<Save />}
            >
              Guardar
            </Button>
            <Button
              variant="outlined"
              onClick={onCloseEdit}
              className={classes.submit}
              style={{ color: '#dc3545' }}
              disabled={isLoading}
              startIcon={<CancelIcon />}
            >
              Cancelar
            </Button>
          </>
        )}
      </div>
    </form>
  );
}
